import React, { useState, useRef, useEffect } from 'react'
import { Session } from '../types'

export interface UserMenuProps {
  session: Session
  onSignOut?: () => void
  onProfileClick?: () => void
  onBillingClick?: () => void
  onSettingsClick?: () => void
  className?: string
  style?: React.CSSProperties
}

export function UserMenu({ 
  session, 
  onSignOut, 
  onProfileClick, 
  onBillingClick, 
  onSettingsClick,
  className,
  style 
}: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSignOut = () => {
    setIsOpen(false)
    onSignOut?.()
  }

  const handleProfileClick = () => {
    setIsOpen(false)
    onProfileClick?.()
  }

  const handleBillingClick = () => {
    setIsOpen(false)
    onBillingClick?.()
  }

  const handleSettingsClick = () => {
    setIsOpen(false)
    onSettingsClick?.()
  }

  const getUserInitials = (userId: string): string => {
    // In a real implementation, you'd get the user's name from the session
    return userId.slice(0, 2).toUpperCase()
  }

  const getPlanBadge = (plan?: string): string => {
    const badges: Record<string, string> = {
      free: 'Free',
      basic: 'Basic',
      pro: 'Pro',
      enterprise: 'Enterprise'
    }
    return badges[plan || 'free'] || 'Free'
  }

  const getPlanColor = (plan?: string): string => {
    const colors: Record<string, string> = {
      free: '#6b7280',
      basic: '#3b82f6',
      pro: '#8b5cf6',
      enterprise: '#f59e0b'
    }
    return colors[plan || 'free'] || '#6b7280'
  }

  return (
    <div className={`user-menu ${className || ''}`} style={style} ref={menuRef}>
      <button
        className="user-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="user-avatar">
          {getUserInitials(session.userId)}
        </div>
        <div className="user-info">
          <div className="user-id">{session.userId}</div>
          <div className="user-plan" style={{ color: getPlanColor(session.plan) }}>
            {getPlanBadge(session.plan)}
          </div>
        </div>
        <div className="user-menu-arrow">
          {isOpen ? '▲' : '▼'}
        </div>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="user-menu-header">
            <div className="user-avatar-large">
              {getUserInitials(session.userId)}
            </div>
            <div className="user-details">
              <div className="user-id">{session.userId}</div>
              <div className="user-plan" style={{ color: getPlanColor(session.plan) }}>
                {getPlanBadge(session.plan)}
              </div>
              {session.orgId && (
                <div className="user-org">Organization: {session.orgId}</div>
              )}
            </div>
          </div>

          <div className="user-menu-divider" />

          <div className="user-menu-items">
            <button className="user-menu-item" onClick={handleProfileClick}>
              <span className="menu-item-icon">👤</span>
              <span className="menu-item-text">Profile</span>
            </button>

            <button className="user-menu-item" onClick={handleBillingClick}>
              <span className="menu-item-icon">💳</span>
              <span className="menu-item-text">Billing</span>
            </button>

            <button className="user-menu-item" onClick={handleSettingsClick}>
              <span className="menu-item-icon">⚙️</span>
              <span className="menu-item-text">Settings</span>
            </button>
          </div>

          <div className="user-menu-divider" />

          <div className="user-menu-items">
            <button className="user-menu-item sign-out" onClick={handleSignOut}>
              <span className="menu-item-icon">🚪</span>
              <span className="menu-item-text">Sign Out</span>
            </button>
          </div>
        </div>
      )}

      <style>{`
        .user-menu {
          position: relative;
          display: inline-block;
        }

        .user-menu-trigger {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .user-menu-trigger:hover {
          border-color: #9ca3af;
          background: #f9fafb;
        }

        .user-avatar {
          width: 2rem;
          height: 2rem;
          border-radius: 50%;
          background: #3b82f6;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .user-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .user-id {
          font-size: 0.875rem;
          font-weight: 500;
          color: #1a1a1a;
        }

        .user-plan {
          font-size: 0.75rem;
          font-weight: 500;
        }

        .user-menu-arrow {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .user-menu-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 0.5rem;
          min-width: 16rem;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          z-index: 50;
        }

        .user-menu-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
        }

        .user-avatar-large {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 50%;
          background: #3b82f6;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .user-details {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .user-org {
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }

        .user-menu-divider {
          height: 1px;
          background: #e5e7eb;
          margin: 0 0.5rem;
        }

        .user-menu-items {
          padding: 0.5rem 0;
        }

        .user-menu-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
          padding: 0.5rem 1rem;
          border: none;
          background: none;
          color: #374151;
          font-size: 0.875rem;
          text-align: left;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .user-menu-item:hover {
          background: #f3f4f6;
        }

        .user-menu-item.sign-out {
          color: #dc2626;
        }

        .user-menu-item.sign-out:hover {
          background: #fef2f2;
        }

        .menu-item-icon {
          font-size: 1rem;
        }

        .menu-item-text {
          flex: 1;
        }
      `}</style>
    </div>
  )
}
